import { Card } from '../common';
import type { DrawnCard, TarotSpread } from '../../types/tarot';
import { getCardMeaning, formatCardName } from '../../utils/tarotLogic';

interface SpreadInterpretationProps {
  cards: DrawnCard[];
  spread: TarotSpread;
  question: string;
  aiInterpretation?: string;
  basicInterpretation?: string;
}

export function SpreadInterpretation({
  cards,
  spread,
  question,
  aiInterpretation,
  basicInterpretation
}: SpreadInterpretationProps) {
  
  const renderCelticCrossInterpretation = () => {
    const sections = [
      {
        title: 'The Cross - Your Current Situation',
        cards: cards.slice(0, 6),
        description: 'The cross formation reveals the core of your situation, from past influences to future possibilities.'
      },
      {
        title: 'The Staff - Your Inner and Outer World',
        cards: cards.slice(6, 10),
        description: 'The staff shows how you approach the situation and the external forces at play.'
      }
    ];

    return (
      <div className="space-y-8">
        {sections.map((section, sectionIndex) => (
          <Card key={sectionIndex} className="p-6">
            <h3 className="text-xl font-bold text-white mb-2">{section.title}</h3>
            <p className="text-gray-400 text-sm mb-4">{section.description}</p>
            
            <div className="space-y-4">
              {section.cards.map((card, cardIndex) => {
                const globalIndex = sectionIndex === 0 ? cardIndex : cardIndex + 6;
                const position = spread.positions[globalIndex];
                const meanings = getCardMeaning(card);
                
                return (
                  <div key={globalIndex} className="border-l-2 border-primary-500/30 pl-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-white">
                          {position.name}: {formatCardName(card)}
                        </h4>
                        <p className="text-gray-400 text-sm mb-2">{position.description}</p>
                        <div className="text-sm text-gray-300">
                          <span className="font-medium">Key meanings:</span> {meanings.slice(0, 3).join(', ')}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>
    );
  };

  const renderThreeCardInterpretation = () => {
    return (
      <Card className="p-6">
        <h3 className="text-xl font-bold text-white mb-4">Timeline Reading</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((card, index) => {
            const position = spread.positions[index];
            const meanings = getCardMeaning(card);
            
            return (
              <div key={index} className="text-center">
                <div className="w-3 h-3 bg-primary-500 rounded-full mx-auto mb-2"></div>
                <h4 className="font-semibold text-white mb-1">{position.name}</h4>
                <h5 className="text-primary-400 font-medium mb-2">{formatCardName(card)}</h5>
                <p className="text-gray-400 text-sm mb-2">{position.description}</p>
                <div className="text-sm text-gray-300">
                  {meanings.slice(0, 2).join(', ')}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    );
  };

  const renderSingleCardInterpretation = () => {
    const card = cards[0];
    const meanings = getCardMeaning(card);
    
    return (
      <Card className="p-6 text-center">
        <h3 className="text-xl font-bold text-white mb-4">Your Guidance</h3>
        <h4 className="text-2xl font-semibold text-primary-400 mb-4">
          {formatCardName(card)}
        </h4>
        <div className="space-y-4">
          <div>
            <h5 className="font-medium text-white mb-2">Key Meanings</h5>
            <div className="flex flex-wrap justify-center gap-2">
              {meanings.slice(0, 4).map((meaning, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-primary-500/20 text-primary-300 rounded-full text-sm"
                >
                  {meaning}
                </span>
              ))}
            </div>
          </div>
          
          <div className="text-gray-300">
            <p className="font-medium mb-2">Reflection</p>
            <p className="text-sm leading-relaxed">
              This card invites you to consider {meanings[0].toLowerCase()} in relation to your question. 
              {card.isReversed 
                ? ' The reversed position suggests looking inward or addressing blocks in this area.'
                : ' The upright position encourages embracing this energy fully.'
              }
            </p>
          </div>
        </div>
      </Card>
    );
  };

  const renderSpreadSpecificInterpretation = () => {
    switch (spread.id) {
      case 'celtic-cross':
        return renderCelticCrossInterpretation();
      case 'three-card':
        return renderThreeCardInterpretation();
      case 'single-card':
        return renderSingleCardInterpretation();
      default:
        return (
          <Card className="p-6">
            <h3 className="text-xl font-bold text-white mb-4">{spread.name} Reading</h3>
            <div className="space-y-4">
              {cards.map((card, index) => {
                const position = spread.positions[index];
                const meanings = getCardMeaning(card);
                
                return (
                  <div key={index} className="border-l-2 border-primary-500/30 pl-4">
                    <h4 className="font-semibold text-white">
                      {position.name}: {formatCardName(card)}
                    </h4>
                    <p className="text-gray-400 text-sm mb-1">{position.description}</p>
                    <div className="text-sm text-gray-300">
                      {meanings.slice(0, 2).join(', ')}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Question */}
      <Card className="p-4 bg-primary-500/10 border border-primary-500/20">
        <h2 className="text-lg font-semibold text-white mb-2">Your Question</h2>
        <p className="text-gray-300 italic">"{question}"</p>
      </Card>

      {/* Spread-specific interpretation */}
      {renderSpreadSpecificInterpretation()}

      {/* AI Interpretation */}
      {aiInterpretation && (
        <Card className="p-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center">
            <span className="mr-2">🤖</span>
            AI Interpretation
          </h3>
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 leading-relaxed whitespace-pre-line">
              {aiInterpretation}
            </p>
          </div>
        </Card>
      )}

      {/* Basic Interpretation */}
      {basicInterpretation && !aiInterpretation && (
        <Card className="p-6">
          <h3 className="text-xl font-bold text-white mb-4">Interpretation</h3>
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 leading-relaxed whitespace-pre-line">
              {basicInterpretation}
            </p>
          </div>
        </Card>
      )}

      {/* Reading Summary */}
      <Card className="p-6 bg-dark-700">
        <h3 className="text-lg font-semibold text-white mb-4">Reading Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-primary-400">{cards.length}</div>
            <div className="text-sm text-gray-400">Cards Drawn</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-primary-400">
              {cards.filter(c => c.arcana === 'major').length}
            </div>
            <div className="text-sm text-gray-400">Major Arcana</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-primary-400">
              {cards.filter(c => c.isReversed).length}
            </div>
            <div className="text-sm text-gray-400">Reversed</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-primary-400">{spread.name}</div>
            <div className="text-sm text-gray-400">Spread Type</div>
          </div>
        </div>
      </Card>
    </div>
  );
}